import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import ElchemyLogoSVG from "./ElchemyLogoSVG";
import ElchemyLogoMobileSVG from "./ElchemyLogoMobileSVG";
import USFlagSVG from "./USFlagSVG";
import IndiaFlagSVG from "./IndiaFlagSVG";
import ContactIconSVG from "./ContactIconSVG";
import styles from "./USHeader.module.scss";
import { writeCachedCountry } from "@/components/us-website/LocationWrapper";
import { ExpandMore, Menu, Close, Language } from "@mui/icons-material";
import WaterTreatmentAndMiningSVG from "./IndustriesSVGs/WaterTreatmentAndMiningSVG";
import PersonalCareSVG from "./IndustriesSVGs/PersonalCareSVG";
import FlavoursAndFragranceSVG from "./IndustriesSVGs/FlavoursAndFragranceSVG";
import OilAndGasSVG from "./IndustriesSVGs/OilAndGasSVG";
import ChemicalIntermediatesSVG from "./IndustriesSVGs/ChemicalIntermediatesSVG";
import FoodAndNutritionSVG from "./IndustriesSVGs/FoodAndNutritionSVG";
import HomeCareAndIndustrialCleaningSVG from "./IndustriesSVGs/HomeCareAndIndustrialCleaningSVG";
import CASESVG from "./IndustriesSVGs/CASESVG";
import BlogSVG from "./CompanySVGs/BlogSVG";
import EventsSVG from "./CompanySVGs/EventsSVG";
import NewsSVG from "./CompanySVGs/NewsSVG";
import ContactUsFormModal from "../ContactUsFormModal/ContactUsFormModal";
import { useSelector, useDispatch } from "react-redux";
import { setUserLocation } from "@/redux/slices/UserLocation";
import MObileBurgerSVG from "./MobileBurgerSVG";
import { useRouter } from "next/router";
import GlobeIconSVG from "./GlobeIconSVG";
import { getCDNImageUrl, getIntlUrl as getIntlUrlUtil } from "@/utils/Common";
import { MARKET_INSIGHTS_ENABLED } from "@/constastants/Constant";

const slugify = (industryName, industryId) => {
  return industryName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric chars with hyphen
    .replace(/^-+|-+$/g, "") // Remove leading/trailing hyphens
    .concat(`-${industryId}`); // Append ID for uniqueness
};

const industriesData = [
  {
    id: 3,
    name: "Personal Care",
    description: "Ingredients for beauty, hygiene, and skincare products.",
    slug: slugify("Personal Care", 3),
    icon: PersonalCareSVG,
  },

  {
    id: 6,
    name: "Home Care & Industrial Cleaning",
    description: "Formulations for powerful, effective cleaning solutions.",
    slug: slugify("Home Care & Industrial Cleaning", 6),
    icon: HomeCareAndIndustrialCleaningSVG,
  },
  {
    id: 5,
    name: "Flavours & Fragrances",
    description: "Aromatics and flavorants for sensory-rich products.",
    slug: slugify("Flavours & Fragrances", 5),
    icon: FlavoursAndFragranceSVG,
  },
  {
    id: 2,
    name: "Oil & Gas",
    description: "Essential inputs for energy and resource extraction.",
    slug: slugify("Oil & Gas", 2),
    icon: OilAndGasSVG,
  },
  {
    id: 4,
    name: "Food & Nutrition",
    description: "Safe, high-quality additives for food and health.",
    slug: slugify("Food & Nutrition", 4),
    icon: FoodAndNutritionSVG,
  },
  {
    id: 7,
    name: "Chemical Intermediates",
    description: "Core materials for diverse chemical manufacturing.",
    slug: slugify("Chemical Intermediates", 7),
    icon: ChemicalIntermediatesSVG,
  },

  {
    id: 8,
    name: "CASE",
    description: "Chemicals for coatings, adhesives, and sealants.",
    slug: slugify("CASE", 8),
    icon: CASESVG,
  },
  {
    id: 1,
    name: "Water Treatment & Mining",
    description: "Chemicals for clean, safe, and efficient water systems.",
    slug: slugify("Water Treatment & Mining", 1),
    icon: WaterTreatmentAndMiningSVG,
  },
];

const companyData = [
  {
    id: 4,
    name: "About Us",
    icon: NewsSVG,
    description: "Latest updates from Elchemy and the industry.",
    url: "/about-us",
  },
  {
    id: 1,
    name: "Blogs",
    icon: BlogSVG,
    description: "Insights and trends shaping the chemical industry.",
    url: "/blogs",
  },
  {
    id: 2,
    name: "Events",
    icon: EventsSVG,
    description: "Key chemical industry events and trade shows.",
    url: "/events",
  },
  {
    id: 3,
    name: "Careers",
    icon: NewsSVG,
    description: "Explore career opportunities at Elchemy.",
    url: "/careers",
  },
  {
    id: 4,
    name: "Market Insights",
    icon: NewsSVG,
    description: "Latest updates from Elchemy and the industry.",
    url: "/market-insights",
  },
].filter((item) => MARKET_INSIGHTS_ENABLED || item.name !== "Market Insights");

const solutionsData = [
  {
    id: 1,
    name: "Custom Manufacturing",
    description: "Tailored chemical manufacturing solutions.",
    url: "/quality-custom-chemical-manufacturing",
  },
  {
    id: 2,
    name: "Tail Spend Management",
    description: "Optimise your tail spend with dedicated sourcing support.",
    url: "/tail-spend-management",
  },
  {
    id: 3,
    name: "Request a Sample",
    description: "Try before you buy with our fast-track sample program.",
    url: "/request-a-sample",
  },
];

function USHeader() {
  const dispatch = useDispatch();
  const { country, loading, error } = useSelector(
    (state) => state.userLocation,
  );
  // Filter companyData based on country
  const filteredCompanyData =
    country === "US"
      ? companyData.filter(
          (item) => item.name !== "Careers" && item.name !== "Market Insights",
        )
      : companyData;
  const router = useRouter();
  const isIntl = router.asPath?.endsWith("/intl");
  const getIntlUrl = (url) => getIntlUrlUtil(url, isIntl);
  // selectedCountry is derived from Redux country value

  const selectedCountry = country === "US" ? "USA" : "India";
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [industriesHover, setIndustriesHover] = useState(false);
  const [companyHover, setCompanyHover] = useState(false);
  const [solutionsHover, setSolutionsHover] = useState(false);

  // Mobile menu states
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null); // 'industries', 'company', 'solutions', 'country', or null

  const industriesTimeoutRef = useRef(null);
  const companyTimeoutRef = useRef(null);
  const solutionsTimeoutRef = useRef(null);

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const toggleCountryDropdown = () => {
    setIsCountryDropdownOpen(!isCountryDropdownOpen);
  };

  // Update country selection logic
  const selectCountry = (countryLabel) => {
    // Map label to code
    const newCountry = countryLabel === "USA" ? "US" : "IN";
    if (country === newCountry) {
      setIsCountryDropdownOpen(false);
      return;
    }
    writeCachedCountry(newCountry);
    // Commenting because it is being dispatched in the LocationWrapper itself and the page is reloaded so this is lost anyway
    dispatch(
      setUserLocation({
        country: newCountry,
        cached: true,
        timestamp: Date.now(),
      }),
    );
    setIsCountryDropdownOpen(false);

    if (router.pathname === "/events") {
      window.location.reload(); // Full refresh for events page
    } else {
      router.push(router.asPath); // Keep original behavior for other pages
    }
  };

  const handleContactUsClick = () => {
    // setIsContactModalOpen(true);
    router.push("/contact-elchemy");
  };

  // Hover handlers for Industries
  const handleIndustriesMouseEnter = () => {
    if (industriesTimeoutRef.current) {
      clearTimeout(industriesTimeoutRef.current);
    }
    setIndustriesHover(true);
  };

  const handleIndustriesMouseLeave = () => {
    industriesTimeoutRef.current = setTimeout(() => {
      setIndustriesHover(false);
    }, 200);
  };

  // Hover handlers for Company
  const handleCompanyMouseEnter = () => {
    if (companyTimeoutRef.current) {
      clearTimeout(companyTimeoutRef.current);
    }
    setCompanyHover(true);
  };

  const handleCompanyMouseLeave = () => {
    companyTimeoutRef.current = setTimeout(() => {
      setCompanyHover(false);
    }, 200);
  };

  // Hover handlers for Solutions
  const handleSolutionsMouseEnter = () => {
    if (solutionsTimeoutRef.current) {
      clearTimeout(solutionsTimeoutRef.current);
    }
    setSolutionsHover(true);
  };

  const handleSolutionsMouseLeave = () => {
    solutionsTimeoutRef.current = setTimeout(() => {
      setSolutionsHover(false);
    }, 200);
  };

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setOpenAccordion(null); // Close all accordions when menu toggles
  };

  const toggleAccordion = (accordionName) => {
    setOpenAccordion(openAccordion === accordionName ? null : accordionName);
  };

  // For mobile
  const selectCountryMobile = (countryLabel) => {
    const newCountry = countryLabel === "USA" ? "US" : "IN";
    if (country === newCountry) {
      setOpenAccordion(null);
      return;
    }
    writeCachedCountry(newCountry);
    dispatch(
      setUserLocation({
        country: newCountry,
        cached: true,
        timestamp: Date.now(),
      }),
    );
    setOpenAccordion(null);
    window.location.reload();
  };

  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false);
    setOpenAccordion(null);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (industriesTimeoutRef.current) {
        clearTimeout(industriesTimeoutRef.current);
      }
      if (companyTimeoutRef.current) {
        clearTimeout(companyTimeoutRef.current);
      }
      if (solutionsTimeoutRef.current) {
        clearTimeout(solutionsTimeoutRef.current);
      }
    };
  }, []);
  // console.log("Selected Country", selectedCountry);
  return (
    <>
      {/* Desktop Header */}
      <header className={`${styles.header} ${styles.desktopHeader}`}>
        {/* Logo */}
        <div className={styles.logoContainer}>
          <Link href="/">
            <ElchemyLogoSVG />
          </Link>
        </div>

        {/* Right Content Container */}
        <div className={styles.rightContentContainer}>
          {/* Navigation Links */}
          <nav className={styles.navigation}>
            <ul className={styles.navList}>
              <li
                className={styles.navItem}
                onMouseEnter={handleIndustriesMouseEnter}
                onMouseLeave={handleIndustriesMouseLeave}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  router.push("/industry");
                }}
              >
                <span className={styles.navLink}>
                  Industries
                  <ExpandMore className={styles.chevronIcon} />
                </span>
                {industriesHover && (
                  <div className={styles.industriesHoverMenu}>
                    <div className={styles.industriesGrid}>
                      {industriesData.map((industry) => {
                        const IconComponent = industry.icon;
                        return (
                          <Link
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            key={industry.id}
                            href={`/industry/${industry.slug}`}
                            className={styles.industryItem}
                          >
                            <div className={styles.industryIcon}>
                              <IconComponent />
                            </div>
                            <div className={styles.industryContent}>
                              <div className={styles.industryName}>
                                {industry.name}
                              </div>
                              <div className={styles.industryDescription}>
                                {industry.description}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </li>
              <li className={styles.navItem}>
                <Link href="/product" className={styles.navLink}>
                  Products
                </Link>
              </li>
              <li
                className={styles.navItem}
                onMouseEnter={handleSolutionsMouseEnter}
                onMouseLeave={handleSolutionsMouseLeave}
              >
                <span className={styles.navLink}>
                  Solutions
                  <ExpandMore className={styles.chevronIcon} />
                </span>
                {solutionsHover && (
                  <div className={styles.solutionsHoverMenu}>
                    <div className={styles.solutionsGrid}>
                      {solutionsData.map((solution) => {
                        return (
                          <Link
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            key={solution.id}
                            href={solution.url}
                            className={styles.solutionItem}
                          >
                            <div className={styles.solutionContent}>
                              <div className={styles.solutionName}>
                                {solution.name}
                              </div>
                              <div className={styles.solutionDescription}>
                                {solution.description}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </li>
              <li className={styles.navItem}>
                <Link
                  href={getIntlUrl("/elchemy-tech")}
                  className={styles.navLink}
                >
                  Our Technology
                </Link>
              </li>
              <li className={styles.navItem}>
                <Link href="/sustainability-elchemy" className={styles.navLink}>
                  Sustainability & Compliance
                </Link>
              </li>
              <li
                className={styles.navItem}
                onMouseEnter={handleCompanyMouseEnter}
                onMouseLeave={handleCompanyMouseLeave}
              >
                <span className={styles.navLink}>
                  Company
                  <ExpandMore className={styles.chevronIcon} />
                </span>
                {companyHover && (
                  <div className={styles.companyHoverMenu}>
                    <div className={styles.companyGrid}>
                      {filteredCompanyData.map((item) => {
                        const IconComponent = item.icon;
                        return (
                          <Link
                            key={item.id}
                            href={getIntlUrl(item.url)}
                            className={styles.companyItem}
                          >
                            <div className={styles.companyIcon}>
                              <IconComponent />
                            </div>
                            <div className={styles.companyContent}>
                              <div className={styles.companyName}>
                                {item.name}
                              </div>
                              <div className={styles.companyDescription}>
                                {item.description}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </li>
            </ul>
          </nav>

          {/* Right Side Actions */}
          <div className={styles.rightActions}>
            {/* Vertical Separator */}
            <div className={styles.separator}></div>

            {/* Country Selector Dropdown */}
            <div className={styles.countrySelector}>
              <button
                className={styles.countryButton}
                onClick={toggleCountryDropdown}
              >
                {selectedCountry === "USA" ? (
                  <USFlagSVG />
                ) : (
                  <Image
                    src={getCDNImageUrl("Global.webp")}
                    alt="Global"
                    width={25}
                    height={25}
                  />
                )}
                <ExpandMore
                  className={`${styles.dropdownArrow} ${
                    isCountryDropdownOpen ? styles.rotated : ""
                  }`}
                />
              </button>

              {isCountryDropdownOpen && (
                <div className={styles.countryDropdown}>
                  <button
                    className={`${styles.countryOption} ${
                      selectedCountry === "USA" ? styles.selected : ""
                    }`}
                    onClick={() => selectCountry("USA")}
                  >
                    <USFlagSVG />
                    <span>United States</span>
                  </button>
                  <button
                    className={`${styles.countryOption} ${
                      selectedCountry !== "USA" ? styles.selected : ""
                    }`}
                    onClick={() => selectCountry("India")}
                  >
                    <Image
                      src={getCDNImageUrl("Global.webp")}
                      alt="Global"
                      width={25}
                      height={25}
                    />
                    <span>Global</span>
                  </button>
                </div>
              )}
            </div>

            {/* Contact Us Button */}
            <button
              className={styles.contactUsButton}
              onClick={handleContactUsClick}
            >
              {/* <ContactIconSVG /> */}
              <span>Contact Us</span>
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Header */}
      <header className={`${styles.header} ${styles.mobileHeader}`}>
        {/* Mobile Logo */}
        <div className={styles.mobileLogoContainer}>
          <Link href="/">
            <ElchemyLogoMobileSVG />
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button className={styles.mobileMenuButton} onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? <Close /> : <MObileBurgerSVG />}
        </button>
      </header>
      {/* Mobile Menu Overlay */}
      <div
        className={`${styles.mobileMenuOverlay} ${
          isMobileMenuOpen ? styles.active : ""
        }`}
        onClick={toggleMobileMenu}
      >
        <div
          className={`${styles.mobileMenu} ${
            isMobileMenuOpen ? styles.open : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.mobileMenuContent}>
            {/* Industries Accordion */}
            <div className={styles.mobileMenuItem}>
              <button
                className={`${styles.mobileAccordionButton} ${
                  openAccordion === "industries" ? styles.open : ""
                }`}
                onClick={() => toggleAccordion("industries")}
              >
                Industries
                <ExpandMore
                  className={`${styles.mobileChevron} ${
                    openAccordion === "industries" ? styles.rotated : ""
                  }`}
                />
              </button>
              {openAccordion === "industries" && (
                <div className={styles.mobileAccordionContent}>
                  <Link
                    href="/industry"
                    className={styles.mobileAccordionItem}
                    onClick={handleMobileLinkClick}
                  >
                    All industries
                  </Link>
                  {industriesData.map((industry) => (
                    <Link
                      key={industry.id}
                      href={`/industry/${industry.slug}`}
                      className={styles.mobileAccordionItem}
                      onClick={handleMobileLinkClick}
                    >
                      {industry.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.mobileSeparator}></div>

            {/* Products Link */}
            <Link
              href="/product"
              className={styles.mobileLink}
              onClick={handleMobileLinkClick}
            >
              Products
            </Link>

            <div className={styles.mobileSeparator}></div>

            {/* Solutions Accordion */}
            <div className={styles.mobileMenuItem}>
              <button
                className={`${styles.mobileAccordionButton} ${
                  openAccordion === "solutions" ? styles.open : ""
                }`}
                onClick={() => toggleAccordion("solutions")}
              >
                Solutions
                <ExpandMore
                  className={`${styles.mobileChevron} ${
                    openAccordion === "solutions" ? styles.rotated : ""
                  }`}
                />
              </button>
              {openAccordion === "solutions" && (
                <div className={styles.mobileAccordionContent}>
                  {solutionsData.map((solution) => (
                    <Link
                      key={solution.id}
                      href={solution.url}
                      className={styles.mobileAccordionItem}
                      onClick={handleMobileLinkClick}
                    >
                      {solution.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.mobileSeparator}></div>

            {/* Our Technology Link */}
            <Link
              href={getIntlUrl("/elchemy-tech")}
              className={styles.mobileLink}
              onClick={handleMobileLinkClick}
            >
              Our Technology
            </Link>

            <div className={styles.mobileSeparator}></div>

            {/* Sustainability and Compliance Link */}
            <Link
              href="/sustainability-elchemy"
              className={styles.mobileLink}
              onClick={handleMobileLinkClick}
            >
              Sustainability & Compliance
            </Link>

            <div className={styles.mobileSeparator}></div>

            {/* Company Accordion */}
            <div className={styles.mobileMenuItem}>
              <button
                className={`${styles.mobileAccordionButton} ${
                  openAccordion === "company" ? styles.open : ""
                }`}
                onClick={() => toggleAccordion("company")}
              >
                Company
                <ExpandMore
                  className={`${styles.mobileChevron} ${
                    openAccordion === "company" ? styles.rotated : ""
                  }`}
                />
              </button>
              {openAccordion === "company" && (
                <div className={styles.mobileAccordionContent}>
                  {filteredCompanyData.map((item) => (
                    <Link
                      key={item.id}
                      href={getIntlUrl(item.url)}
                      className={styles.mobileAccordionItem}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.mobileSeparator}></div>

            {/* Country Accordion */}
            <div className={styles.mobileMenuItem}>
              <button
                className={`${styles.mobileAccordionButton} ${
                  openAccordion === "country" ? styles.open : ""
                }`}
                onClick={() => toggleAccordion("country")}
              >
                <span className={styles.countryDisplay}>
                  {selectedCountry === "USA" ? (
                    <USFlagSVG />
                  ) : (
                    <Image
                      src={getCDNImageUrl("Global.webp")}
                      alt="Global"
                      width={25}
                      height={25}
                    />
                  )}
                  {selectedCountry === "USA" ? "United States" : "Global"}
                </span>
                <ExpandMore
                  className={`${styles.mobileChevron} ${
                    openAccordion === "country" ? styles.rotated : ""
                  }`}
                />
              </button>
              {openAccordion === "country" && (
                <div className={styles.mobileAccordionContent}>
                  <button
                    className={`${styles.mobileCountryOption} ${
                      selectedCountry === "USA" ? styles.selected : ""
                    }`}
                    onClick={() => selectCountryMobile("USA")}
                  >
                    <USFlagSVG />
                    <span>United States</span>
                  </button>
                  <button
                    className={`${styles.mobileCountryOption} ${
                      selectedCountry !== "USA" ? styles.selected : ""
                    }`}
                    onClick={() => selectCountryMobile("Global")}
                  >
                    <Image
                      src={getCDNImageUrl("Global.webp")}
                      alt="Global"
                      width={25}
                      height={25}
                    />
                    <span>Global</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Contact Us Button */}
          <button
            className={styles.mobileContactButton}
            onClick={() => {
              handleContactUsClick();
              handleMobileLinkClick();
            }}
          >
            {/* <ContactIconSVG /> */}
            <span>Contact Us</span>
          </button>
        </div>
      </div>
      {/* Contact Us Modal */}
      <ContactUsFormModal
        open={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </>
  );
}

export default USHeader;
